import React, { useEffect, useRef } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  DEFAULT_FARMLAND_FILTER,
  FARMLAND_AREA_RANGE_OPTIONS,
  FARMLAND_CATEGORICAL_FILTER_CONFIGS,
  FARMLAND_END_STAGES_DATE_RANGE_OPTIONS,
} from "@/features/map/constants/farmland";
import {
  type FarmlandAreaUnit,
  type FarmlandCategoricalFilterConfig,
  type FarmlandEndStagesDateRangeId,
  type FarmlandFilterOption,
  type FarmlandFilterValues,
} from "@/features/map/types/farmland";
import {
  addMonths,
  addYears,
  format,
  isAfter,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";

interface Props {
  open: boolean;
  initialFilter: FarmlandFilterValues;
  onClose: () => void;
  onApply: (filter: FarmlandFilterValues) => void;
}

interface EndStagesDateRange {
  from: string | null;
  to: string | null;
}

const NO_SELECTION_VALUE = "";

const selectedOptions = (
  config: FarmlandCategoricalFilterConfig,
  selectedIds: string[] | undefined
): FarmlandFilterOption[] => {
  const selectedIdSet = new Set(selectedIds ?? []);
  return config.options.filter((option) => selectedIdSet.has(option.id));
};

const getToday = (): Date => startOfDay(new Date());

const formatFilterDate = (date: Date): string => format(date, "yyyy-MM-dd");

const resolveEndStagesDateRange = (
  rangeId: FarmlandEndStagesDateRangeId,
  baseDate: Date = getToday()
): EndStagesDateRange => {
  const today = formatFilterDate(baseDate);

  switch (rangeId) {
    case "withinThreeMonths":
      return { from: today, to: formatFilterDate(addMonths(baseDate, 3)) };
    case "withinSixMonths":
      return { from: today, to: formatFilterDate(addMonths(baseDate, 6)) };
    case "withinOneYear":
      return { from: today, to: formatFilterDate(addYears(baseDate, 1)) };
    case "withinTwoYears":
      return { from: today, to: formatFilterDate(addYears(baseDate, 2)) };
    case "withinThreeYears":
      return { from: today, to: formatFilterDate(addYears(baseDate, 3)) };
    case "overThreeYears":
      return { from: formatFilterDate(addYears(baseDate, 3)), to: null };
  }
};

const getSelectedAreaRangeId = (filter: FarmlandFilterValues): string => {
  const selectedOption = FARMLAND_AREA_RANGE_OPTIONS[filter.areaUnit].find(
    (option) => option.from === filter.areaFrom && option.to === filter.areaTo
  );
  return selectedOption?.id ?? NO_SELECTION_VALUE;
};

const getSelectedEndStagesDateRangeId = (
  filter: FarmlandFilterValues,
  baseDate: Date = getToday()
): string => {
  const selectedOption = FARMLAND_END_STAGES_DATE_RANGE_OPTIONS.find(
    (option) => {
      const range = resolveEndStagesDateRange(option.id, baseDate);
      return (
        range.from === filter.endStagesDateFrom &&
        range.to === filter.endStagesDateTo
      );
    }
  );
  return selectedOption?.id ?? NO_SELECTION_VALUE;
};

const normalizeSelectableFilter = (
  filter: FarmlandFilterValues,
  baseDate: Date = getToday()
): FarmlandFilterValues => {
  const hasAreaFilter = filter.areaFrom !== null || filter.areaTo !== null;
  const hasEndStagesDateFilter =
    filter.endStagesDateFrom !== null || filter.endStagesDateTo !== null;
  const selectedAreaRangeId = getSelectedAreaRangeId(filter);
  const selectedEndStagesDateRangeId = getSelectedEndStagesDateRangeId(
    filter,
    baseDate
  );

  return {
    ...filter,
    areaFrom:
      hasAreaFilter && selectedAreaRangeId === NO_SELECTION_VALUE
        ? null
        : filter.areaFrom,
    areaTo:
      hasAreaFilter && selectedAreaRangeId === NO_SELECTION_VALUE
        ? null
        : filter.areaTo,
    endStagesDateFrom:
      hasEndStagesDateFilter &&
      selectedEndStagesDateRangeId === NO_SELECTION_VALUE
        ? null
        : filter.endStagesDateFrom,
    endStagesDateTo:
      hasEndStagesDateFilter &&
      selectedEndStagesDateRangeId === NO_SELECTION_VALUE
        ? null
        : filter.endStagesDateTo,
  };
};

const parseDateOrNull = (value: string | null): Date | null => {
  if (value === null) return null;
  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : null;
};

const FarmlandFilterFormSchema: z.ZodType<FarmlandFilterValues> = z
  .object({
    classificationOfLandCodes: z.array(z.string()),
    areaUnit: z.enum(["squareMeter", "are"]),
    areaFrom: z.number().nullable(),
    areaTo: z.number().nullable(),
    ownerFarmIntentionCodes: z.array(z.string()),
    usageSituationInvestigationResultCodes: z.array(z.string()),
    ownerStatementIntentSurveyResultsCodes: z.array(z.string()),
    sectionOfNoushinhouCodes: z.array(z.string()),
    sectionOfToshikeikakuhouCodes: z.array(z.string()),
    kindOfRightCodes: z.array(z.string()),
    endStagesDateFrom: z.string().nullable(),
    endStagesDateTo: z.string().nullable(),
    rightSettingContentsCodes: z.array(z.string()),
  })
  .superRefine((filter, context) => {
    if (filter.areaFrom !== null && filter.areaFrom < 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["areaFrom"],
        message: "面積には0以上の値を入力してください。",
      });
    }
    if (filter.areaTo !== null && filter.areaTo < 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["areaTo"],
        message: "面積には0以上の値を入力してください。",
      });
    }
    if (
      filter.areaFrom !== null &&
      filter.areaTo !== null &&
      filter.areaFrom > filter.areaTo
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["areaTo"],
        message: "面積の下限は上限以下にしてください。",
      });
    }

    const endStagesDateFrom = parseDateOrNull(filter.endStagesDateFrom);
    const endStagesDateTo = parseDateOrNull(filter.endStagesDateTo);
    if (filter.endStagesDateFrom !== null && endStagesDateFrom === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endStagesDateFrom"],
        message: "終期年月日（以降）には有効な日付を入力してください。",
      });
    }
    if (filter.endStagesDateTo !== null && endStagesDateTo === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endStagesDateTo"],
        message: "終期年月日（以前）には有効な日付を入力してください。",
      });
    }
    if (
      endStagesDateFrom !== null &&
      endStagesDateTo !== null &&
      isAfter(endStagesDateFrom, endStagesDateTo)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endStagesDateFrom"],
        message: "終期年月日の開始日は終了日以前にしてください。",
      });
    }
  });

export const CFarmlandFilterDialog: React.FC<Props> = ({
  open,
  initialFilter,
  onClose,
  onApply,
}) => {
  const baseDateRef = useRef(getToday());
  const { formState, handleSubmit, reset, setValue, watch } =
    useForm<FarmlandFilterValues>({
      mode: "all",
      defaultValues: normalizeSelectableFilter(
        initialFilter,
        baseDateRef.current
      ),
      resolver: zodResolver(FarmlandFilterFormSchema),
    });
  const draftFilter = watch();
  const validationMessages = Array.from(
    new Set(
      [
        formState.errors.areaFrom?.message,
        formState.errors.areaTo?.message,
        formState.errors.endStagesDateFrom?.message,
        formState.errors.endStagesDateTo?.message,
      ].filter((message): message is string => typeof message === "string")
    )
  );

  useEffect(() => {
    if (open) {
      baseDateRef.current = getToday();
      reset(normalizeSelectableFilter(initialFilter, baseDateRef.current));
    }
  }, [initialFilter, open, reset]);

  const handleClose = (
    _event: React.SyntheticEvent,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick") return;
    onClose();
  };

  const submitFilter = handleSubmit((filter) => {
    onApply(filter);
    onClose();
  });

  const handleReset = (): void => {
    reset(DEFAULT_FARMLAND_FILTER);
  };

  const handleAreaUnitChange = (
    event: SelectChangeEvent<FarmlandAreaUnit>
  ): void => {
    setValue("areaUnit", event.target.value as FarmlandAreaUnit, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("areaFrom", null, { shouldDirty: true, shouldValidate: true });
    setValue("areaTo", null, { shouldDirty: true, shouldValidate: true });
  };

  const areaRangeOptions = FARMLAND_AREA_RANGE_OPTIONS[draftFilter.areaUnit];
  const selectedAreaRangeId = getSelectedAreaRangeId(draftFilter);
  const selectedEndStagesDateRangeId = getSelectedEndStagesDateRangeId(
    draftFilter,
    baseDateRef.current
  );

  const handleAreaRangeChange = (event: SelectChangeEvent<string>): void => {
    const selectedOption = areaRangeOptions.find(
      (option) => option.id === event.target.value
    );
    setValue("areaFrom", selectedOption?.from ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("areaTo", selectedOption?.to ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleEndStagesDateRangeChange = (
    event: SelectChangeEvent<string>
  ): void => {
    const selectedOption = FARMLAND_END_STAGES_DATE_RANGE_OPTIONS.find(
      (option) => option.id === event.target.value
    );
    const range = selectedOption
      ? resolveEndStagesDateRange(selectedOption.id, baseDateRef.current)
      : { from: null, to: null };

    setValue("endStagesDateFrom", range.from, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("endStagesDateTo", range.to, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">農地の条件設定</Typography>
          <Button variant="outlined" onClick={onClose}>
            × 閉じる
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {validationMessages.length > 0 && (
            <Alert severity="error">
              <Stack spacing={0.5}>
                {validationMessages.map((message) => (
                  <Typography key={message} variant="body2">
                    {message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              面積
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <Select
                    value={draftFilter.areaUnit}
                    onChange={handleAreaUnitChange}
                  >
                    <MenuItem value="squareMeter">面積(m²)</MenuItem>
                    <MenuItem value="are">面積(a)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth>
                  <Select
                    value={selectedAreaRangeId}
                    onChange={handleAreaRangeChange}
                    displayEmpty
                  >
                    <MenuItem value={NO_SELECTION_VALUE}>指定しない</MenuItem>
                    {areaRangeOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              終期年月日
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth>
                  <Select
                    value={selectedEndStagesDateRangeId}
                    onChange={handleEndStagesDateRangeChange}
                    displayEmpty
                  >
                    <MenuItem value={NO_SELECTION_VALUE}>指定しない</MenuItem>
                    {FARMLAND_END_STAGES_DATE_RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {FARMLAND_CATEGORICAL_FILTER_CONFIGS.map((config) => (
            <Box key={config.filterKey}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1.5 }}
              >
                {config.title}
              </Typography>
              <Autocomplete
                multiple
                options={config.options}
                value={selectedOptions(config, draftFilter[config.filterKey])}
                onChange={(_, value) => {
                  setValue(
                    config.filterKey,
                    value.map((option) => option.id),
                    { shouldDirty: true, shouldValidate: true }
                  );
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField {...params} placeholder="指定しない" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleReset}>
          条件をクリア
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void submitFilter();
          }}
        >
          指定した条件で絞り込む
        </Button>
      </DialogActions>
    </Dialog>
  );
};
